import React, { useEffect, useMemo, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { io } from 'socket.io-client';
import AppShell from './AppShell';
import Login from './components/Login';
import { ToastProvider } from './components/ui/Toast';
import DashboardPage from './pages/DashboardPage';
import PlantsPage from './pages/PlantsPage';
import PlantDetailPage from './pages/PlantDetailPage';
import PanelDetailPage from './pages/PanelDetailPage';
import ProductionForecastPage from './pages/ProductionForecastPage';
import EnvironmentalImpactPage from './pages/EnvironmentalImpactPage';
import HistoryPage from './pages/HistoryPage';
import AlertsPage from './pages/AlertsPage';
import SettingsPage from './pages/SettingsPage';
import UsersPage from './pages/admin/UsersPage';
import PlantsAdminPage from './pages/admin/PlantsAdminPage';
import AssignmentsPage from './pages/admin/AssignmentsPage';
import AlertRulesPage from './pages/admin/AlertRulesPage';
import DevicesPage from './pages/admin/DevicesPage';
import SystemHealthPage from './pages/admin/SystemHealthPage';
import { useAuth, authHeaders } from './auth';
import './App.css';

const API_URL = process.env.REACT_APP_API_URL || '';
const API_BASE = `${API_URL}/api`;

function RequireAuth({ children }) {
  const { isAuthenticated } = useAuth();
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return children;
}

function AdminRoute({ children }) {
  const { user } = useAuth();
  if (user?.role !== 'admin') return <Navigate to="/" replace />;
  return children;
}

function AppShellWithData() {
  const { isAuthenticated, token, logout } = useAuth();
  const socket = useMemo(() => {
    if (!isAuthenticated) return null;
    // socket.io-client's URL parser only falls back to window.location when
    // the uri argument is null/undefined - an empty string takes a
    // different branch and resolves to a host-less URI (confirmed via a
    // direct trace of socket.io-client's url.js: uri="" produces
    // {host: "", port: "80", id: "http://:80"} instead of the current
    // origin). API_URL is always "" here (REACT_APP_API_URL is never set,
    // same-origin nginx proxy is used instead), so this must be `|| undefined`,
    // not just API_URL, to actually hit the same-origin fallback.
    return io(API_URL || undefined, {
      auth: { token },
      extraHeaders: authHeaders(token),
    });
  }, [isAuthenticated, token]);

  const [plants, setPlants] = useState([]);
  const [realTimeData, setRealTimeData] = useState(new Map());
  const [weatherData, setWeatherData] = useState(new Map());
  const [stats, setStats] = useState({});

  useEffect(() => {
    if (!isAuthenticated) return;
    fetchPlants();
    fetchStats();

    if (socket) {
      socket.on('sensor_data', (data) => {
        setRealTimeData((prev) => {
          const newData = new Map(prev);
          const key = `${data.plant_id}-${data.panel_id}`;
          newData.set(key, data);
          return newData;
        });
      });

      socket.on('weather_data', (payload) => {
        const { plant_id, station_id, data } = payload || {};
        if (!plant_id || !station_id) return;
        setWeatherData((prev) => {
          const map = new Map(prev);
          const key = `${plant_id}-${station_id}`;
          map.set(key, data);
          return map;
        });
      });

      socket.on('panel_status', (status) => {
        console.log('Panel status update:', status);
      });
    }

    const interval = setInterval(() => {
      fetchStats();
    }, 10000);

    return () => {
      if (socket) {
        socket.off('sensor_data');
        socket.off('weather_data');
        socket.off('panel_status');
        socket.close();
      }
      clearInterval(interval);
    };
  }, [isAuthenticated, socket]);

  const fetchPlants = async () => {
    try {
      const response = await fetch(`${API_BASE}/plants`, {
        headers: { 'Content-Type': 'application/json', ...authHeaders(token) },
      });
      if (response.status === 401) {
        logout();
        return;
      }
      if (!response.ok) {
        console.error('Error fetching plants:', response.status, response.statusText);
        return;
      }
      const data = await response.json();
      if (Array.isArray(data)) {
        setPlants(data);
        data.forEach(p => fetchWeather(p.plant_id));
      }
    } catch (error) {
      console.error('Error fetching plants:', error);
    }
  };

  const fetchWeather = async (plantId) => {
    try {
      const response = await fetch(`${API_BASE}/plants/${plantId}/weather/current`, {
        headers: { 'Content-Type': 'application/json', ...authHeaders(token) },
      });
      if (!response.ok) return;
      const arr = await response.json();
      if (Array.isArray(arr)) {
        setWeatherData(prev => {
          const map = new Map(prev);
          arr.forEach(w => {
            const key = `${w.plant_id || plantId}-${w.station_id || w.data?.station_id || 'WS1'}`;
            map.set(key, w.data || w);
          });
          return map;
        });
      }
    } catch (e) {
      console.warn('No weather for plant', plantId, e.message);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_BASE}/dashboard/stats`, {
        headers: { 'Content-Type': 'application/json', ...authHeaders(token) },
      });
      if (response.status === 401) {
        logout();
        return;
      }
      if (!response.ok) {
        console.error('Error fetching stats:', response.status, response.statusText);
        return;
      }
      const data = await response.json();
      if (data && typeof data === 'object') setStats(data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  return <AppShell realTimeData={realTimeData} weatherData={weatherData} plants={plants} stats={stats} socket={socket} />;
}

function AppRoutes() {
  const { isAuthenticated, loading } = useAuth();

  if (loading) return null;

  return (
    <Routes>
      <Route path="/login" element={isAuthenticated ? <Navigate to="/" replace /> : <Login />} />
      <Route path="/" element={<RequireAuth><AppShellWithData /></RequireAuth>}>
        <Route index element={<DashboardPage />} />
        <Route path="plants" element={<PlantsPage />} />
        <Route path="plants/:plantId" element={<PlantDetailPage />} />
        <Route path="plants/:plantId/panels/:panelId" element={<PanelDetailPage />} />
        <Route path="production-forecast" element={<ProductionForecastPage />} />
        <Route path="environmental-impact" element={<EnvironmentalImpactPage />} />
        <Route path="history" element={<HistoryPage />} />
        <Route path="alerts" element={<AlertsPage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="admin/users" element={<AdminRoute><UsersPage /></AdminRoute>} />
        <Route path="admin/plants" element={<AdminRoute><PlantsAdminPage /></AdminRoute>} />
        <Route path="admin/assignments" element={<AdminRoute><AssignmentsPage /></AdminRoute>} />
        <Route path="admin/alert-rules" element={<AdminRoute><AlertRulesPage /></AdminRoute>} />
        <Route path="admin/devices" element={<AdminRoute><DevicesPage /></AdminRoute>} />
        <Route path="admin/system-health" element={<AdminRoute><SystemHealthPage /></AdminRoute>} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ToastProvider>
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </ToastProvider>
  );
}

export default App;
